import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  getAvailableFormatsForGame, 
  validateDeckFormat,
  executeFetchValidatedCards,
  ImportFormatOption,
  DeckValidationResult,
  ImportDeckResult 
} from '../../utils/deckImporter';
import { getGameIcon } from '../../services/supabaseService';
import { getGameNameById } from '../../constants/deckRules';

interface ImportDeckModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameSlug: string;
  activeGame: number | string;
  onImportComplete: (items: any[], replaceCurrent: boolean, importedDeckName?: string, notFoundItems?: any[]) => void;
}

export const ImportDeckModal: React.FC<ImportDeckModalProps> = ({
  isOpen,
  onClose,
  gameSlug,
  activeGame,
  onImportComplete
}) => {
  // Step: 1 = Form & Validation, 2 = Review & Fetch Cards
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);

  const [formatOptions, setFormatOptions] = useState<ImportFormatOption[]>([]);
  const [selectedFormatId, setSelectedFormatId] = useState<string>('');
  const [rawText, setRawText] = useState<string>('');
  const [suggestedDeckName, setSuggestedDeckName] = useState<string>('');
  const [replaceExisting, setReplaceExisting] = useState<boolean>(true);

  // Validation State (Step 1 -> Step 2)
  const [validationResult, setValidationResult] = useState<DeckValidationResult | null>(null);

  // Card Fetching & Loading State (Step 2)
  const [isFetchingCards, setIsFetchingCards] = useState<boolean>(false);
  const [progress, setProgress] = useState<{ current: number; total: number; cardName: string }>({
    current: 0,
    total: 0,
    cardName: ''
  });
  const [importResult, setImportResult] = useState<ImportDeckResult | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const [hasCopiedMissing, setHasCopiedMissing] = useState<boolean>(false);

  // Reset when modal opens or game changes
  useEffect(() => {
    if (isOpen) {
      const formats = getAvailableFormatsForGame(gameSlug);
      setFormatOptions(formats);
      if (formats.length > 0) {
        setSelectedFormatId(formats[0].id);
      }
      setCurrentStep(1);
      setRawText('');
      setSuggestedDeckName('');
      setValidationResult(null);
      setImportResult(null);
      setErrorBanner(null);
      setIsFetchingCards(false);
      setHasCopiedMissing(false);
      setProgress({ current: 0, total: 0, cardName: '' });
    }
  }, [isOpen, gameSlug]);

  const currentFormat = formatOptions.find(f => f.id === selectedFormatId) || formatOptions[0];
  const isFormatAvailable = currentFormat?.isAvailable ?? false;
  const gameName = getGameNameById(activeGame);
  const gameIcon = getGameIcon(activeGame);

  const autoDetectFormat = (text: string) => {
    if (!text) return;
    const lower = text.toLowerCase();
    if (
      lower.includes('pokemoncard.io') || 
      lower.includes('pokemoncard') || 
      (formatOptions.some(f => f.id === 'pokemoncard-io') && /^\d+\s+[A-Za-z0-9\s'.]+?\s+[a-z0-9]+-[0-9]+/m.test(text))
    ) {
      if (formatOptions.some(f => f.id === 'pokemoncard-io')) {
        setSelectedFormatId('pokemoncard-io');
      }
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setRawText(text);
        autoDetectFormat(text);
        setErrorBanner(null);
        setValidationResult(null);
        setImportResult(null);
      }
    } catch (err) {
      console.warn('Clipboard read error:', err);
    }
  };

  const handleLoadExample = () => {
    if (currentFormat && currentFormat.placeholder) {
      setRawText(currentFormat.placeholder);
      setErrorBanner(null);
      setValidationResult(null);
      setImportResult(null);
    }
  };

  const handleCopyMissingCards = () => {
    if (!importResult || importResult.notFoundItems.length === 0) return;
    const text = importResult.notFoundItems.map(item => {
      if (item.code) {
        return `${item.quantity}x${item.code}`;
      }
      return `${item.quantity} ${item.name}`;
    }).join('\n');

    navigator.clipboard.writeText(text);
    setHasCopiedMissing(true);
    setTimeout(() => setHasCopiedMissing(false), 2500);
  };

  // ETAPA 1: Executar Validação do Formato
  const handleValidateStep = () => {
    setErrorBanner(null);
    setImportResult(null);

    if (!isFormatAvailable) {
      setErrorBanner('Função indisponível para este jogo no momento.');
      return;
    }

    if (!rawText.trim()) {
      setErrorBanner('Por favor, cole a lista de cartas do seu baralho na caixa de texto.');
      return;
    }

    const validation = validateDeckFormat(gameSlug, selectedFormatId, rawText);
    setValidationResult(validation);

    if (validation.detectedName && !suggestedDeckName.trim()) {
      setSuggestedDeckName(validation.detectedName);
    }

    if (!validation.isValid) {
      setErrorBanner(validation.errorMessage || 'Formato inválido ou nenhuma carta reconhecida.');
      return;
    }

    // Advance to Step 2: Confirmation & Fetching
    setCurrentStep(2);
  };

  // ETAPA 2: Executar a Busca das Cartas
  const handleFetchCardsStep = async () => {
    if (!validationResult || !validationResult.isValid) {
      setErrorBanner('Validação pendente ou inválida. Volte para a etapa 1.');
      return;
    }

    setIsFetchingCards(true);
    setErrorBanner(null);
    setImportResult(null);

    try {
      const result = await executeFetchValidatedCards(
        gameSlug,
        validationResult.parsedCards,
        (current, total, cardName) => {
          setProgress({ current, total, cardName });
        }
      );

      setImportResult(result);

      if (result.success && result.importedItems.length > 0) {
        onImportComplete(result.importedItems, replaceExisting, suggestedDeckName.trim() || undefined, result.notFoundItems);
        
        // If all cards were found with 0 missing, automatically close modal after a tiny delay
        if (result.notFoundItems.length === 0) {
          setTimeout(() => {
            onClose();
          }, 800);
        }
      } else {
        setErrorBanner(result.errorMessage || 'Nenhuma carta foi encontrada no banco de dados do jogo com os códigos/nomes fornecidos.');
      }
    } catch (err: any) {
      console.error('Error fetching validated cards:', err);
      setErrorBanner('Ocorreu um erro de comunicação ao consultar as cartas no banco de dados. Verifique a conexão e tente novamente.');
    } finally {
      setIsFetchingCards(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div 
        id="import-deck-modal-backdrop"
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget && !isFetchingCards) {
            onClose();
          }
        }}
      >
        <motion.div
          id="import-deck-modal-dialog"
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-2xl bg-slate-900 border border-purple-500/30 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 overflow-hidden max-h-[92vh] flex flex-col justify-between"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
            <div className="space-y-1">
              <div className="flex items-center space-x-2">
                <span className="px-2.5 py-0.5 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-300 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1.5">
                  <i className={gameIcon}></i>
                  <span>{gameName}</span>
                </span>
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                  Assistente de Importação
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-white uppercase italic tracking-tight">
                Importar Baralho
              </h2>
            </div>

            <button
              id="import-deck-close-button"
              onClick={onClose}
              disabled={isFetchingCards}
              className="p-2 text-slate-400 hover:text-white rounded-xl bg-slate-950/50 border border-white/5 hover:border-white/20 transition-all disabled:opacity-50"
            >
              <i className="fas fa-times text-sm"></i>
            </button>
          </div>

          {/* 2-Step Indicator */}
          <div className="grid grid-cols-2 gap-3 p-1.5 bg-slate-950/60 rounded-2xl border border-white/5">
            <button
              type="button"
              onClick={() => {
                if (!isFetchingCards) setCurrentStep(1);
              }}
              className={`flex items-center justify-center space-x-2 py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all ${
                currentStep === 1 
                  ? 'bg-purple-600/30 border border-purple-500/50 text-white shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${
                currentStep === 1 ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-400'
              }`}>
                1
              </span>
              <span>1. Validação do Formato</span>
            </button>

            <button
              type="button"
              disabled={!validationResult?.isValid || isFetchingCards}
              onClick={() => {
                if (validationResult?.isValid && !isFetchingCards) setCurrentStep(2);
              }}
              className={`flex items-center justify-center space-x-2 py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                currentStep === 2 
                  ? 'bg-purple-600/30 border border-purple-500/50 text-white shadow-sm' 
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${
                currentStep === 2 ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-400'
              }`}>
                2
              </span>
              <span>2. Busca das Cartas</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="space-y-5 overflow-y-auto flex-1 pr-1 custom-scrollbar">
            
            {/* ================= ETAPA 1: ENTRADA E VALIDAÇÃO DO FORMATO ================= */}
            {currentStep === 1 && (
              <div className="space-y-4 animate-fadeIn">
                {/* Select do Formato */}
                <div className="space-y-1.5">
                  <label 
                    htmlFor="import-format-select"
                    className="text-[10px] font-black text-slate-400 uppercase tracking-widest block"
                  >
                    Selecione o Formato / Método do Jogo:
                  </label>
                  <div className="relative">
                    <select
                      id="import-format-select"
                      value={selectedFormatId}
                      onChange={(e) => {
                        setSelectedFormatId(e.target.value);
                        setErrorBanner(null);
                        setValidationResult(null);
                      }}
                      className="w-full bg-slate-950/80 border border-white/10 text-slate-200 text-xs rounded-xl px-3.5 py-3 focus:outline-none focus:border-purple-500 appearance-none font-bold pr-10 cursor-pointer"
                    >
                      {formatOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.label} {!opt.isAvailable ? '(Indisponível)' : ''}
                        </option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-xs pointer-events-none"></i>
                  </div>

                  {currentFormat?.description && (
                    <p className="text-[11px] text-slate-400 italic">
                      {currentFormat.description}
                    </p>
                  )}
                </div>

                {/* Aviso quando formato não está programado */}
                {!isFormatAvailable && (
                  <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-start space-x-3.5">
                    <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0 mt-0.5">
                      <i className="fas fa-tools text-base"></i>
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-xs font-black text-amber-300 uppercase tracking-wider">
                        Função Indisponível para este jogo
                      </h4>
                      <p className="text-[11px] text-slate-300 leading-relaxed font-medium">
                        A rotina de validação e importação para <strong>{gameName}</strong> ainda não foi programada no sistema.
                      </p>
                    </div>
                  </div>
                )}

                {/* Campo opcional de Nome do Baralho */}
                {isFormatAvailable && (
                  <div className="space-y-1.5">
                    <label 
                      htmlFor="import-deck-name-input"
                      className="text-[10px] font-black text-slate-400 uppercase tracking-widest block"
                    >
                      Nome do Baralho (Opcional):
                    </label>
                    <input
                      id="import-deck-name-input"
                      type="text"
                      value={suggestedDeckName}
                      onChange={(e) => setSuggestedDeckName(e.target.value)}
                      placeholder="Ex: Baralho Competitivo..."
                      className="w-full bg-slate-950/80 border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-colors font-medium"
                    />
                  </div>
                )}

                {/* Textarea da lista */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label 
                      htmlFor="import-deck-textarea"
                      className="text-[10px] font-black text-slate-400 uppercase tracking-widest block"
                    >
                      Cole o Texto do Baralho:
                    </label>

                    {isFormatAvailable && (
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={handlePasteFromClipboard}
                          className="text-[10px] font-bold text-purple-400 hover:text-purple-300 uppercase tracking-wider flex items-center space-x-1 transition-colors"
                        >
                          <i className="fas fa-paste text-[9px]"></i>
                          <span>Colar</span>
                        </button>
                        <span className="text-slate-600">•</span>
                        <button
                          type="button"
                          onClick={handleLoadExample}
                          className="text-[10px] font-bold text-slate-400 hover:text-slate-200 uppercase tracking-wider flex items-center space-x-1 transition-colors"
                        >
                          <i className="fas fa-magic text-[9px]"></i>
                          <span>Exemplo</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <textarea
                    id="import-deck-textarea"
                    rows={7}
                    value={rawText}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRawText(val);
                      autoDetectFormat(val);
                      setErrorBanner(null);
                      setValidationResult(null);
                    }}
                    disabled={!isFormatAvailable}
                    placeholder={
                      isFormatAvailable 
                        ? (currentFormat?.placeholder || 'Cole a lista de cartas aqui...') 
                        : 'Função indisponível para este jogo no momento.'
                    }
                    className="w-full bg-slate-950/90 border border-white/10 rounded-2xl p-3.5 font-mono text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-all resize-y custom-scrollbar disabled:opacity-40 disabled:cursor-not-allowed leading-relaxed"
                  />
                </div>

                {/* Opções de substituição */}
                {isFormatAvailable && (
                  <div className="flex items-center space-x-4 pt-1">
                    <label className="flex items-center space-x-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="import-mode-step1"
                        checked={replaceExisting}
                        onChange={() => setReplaceExisting(true)}
                        className="w-3.5 h-3.5 text-purple-600 bg-slate-950 border-white/20 focus:ring-purple-500"
                      />
                      <span className="text-xs font-bold text-slate-300">
                        Substituir baralho atual
                      </span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer select-none">
                      <input
                        type="radio"
                        name="import-mode-step1"
                        checked={!replaceExisting}
                        onChange={() => setReplaceExisting(false)}
                        className="w-3.5 h-3.5 text-purple-600 bg-slate-950 border-white/20 focus:ring-purple-500"
                      />
                      <span className="text-xs font-bold text-slate-300">
                        Mesclar com cartas existentes
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* ================= ETAPA 2: REVISÃO DA VALIDAÇÃO E BUSCA DAS CARTAS ================= */}
            {currentStep === 2 && validationResult && (
              <div className="space-y-4 animate-fadeIn">
                {/* Resumo da Validação do Formato */}
                <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                        <i className="fas fa-check text-xs"></i>
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-emerald-300 uppercase tracking-wider">
                          Formato Validado com Sucesso
                        </h4>
                        <p className="text-[10px] text-slate-400">
                          {validationResult.formatLabel}
                        </p>
                      </div>
                    </div>

                    <div className="text-right">
                      <span className="text-base font-black text-emerald-400 font-mono">
                        {validationResult.totalCardsCount}
                      </span>
                      <span className="text-[10px] text-slate-400 block font-bold uppercase">
                        Cartas no Total
                      </span>
                    </div>
                  </div>

                  {/* Detalhamento de Seções Encontradas */}
                  {validationResult.sectionsSummary.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1 border-t border-emerald-500/20">
                      {validationResult.sectionsSummary.map((sec, idx) => (
                        <span 
                          key={idx}
                          className="px-2.5 py-1 rounded-lg bg-slate-950/60 border border-white/5 text-[10px] font-bold text-slate-300 flex items-center space-x-1.5"
                        >
                          <span className="text-emerald-400">{sec.section}:</span>
                          <span className="font-mono text-white">{sec.count} cartas</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Avisos sintáticos (linhas ignoradas) se houver */}
                {validationResult.warnings.length > 0 && (
                  <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center space-x-2 font-medium">
                    <i className="fas fa-exclamation-triangle text-amber-400 shrink-0"></i>
                    <span>{validationResult.warnings.join(' ')}</span>
                  </div>
                )}

                {/* Lista prévia das cartas validadas */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-slate-400">
                    <span>Cartas Identificadas ({validationResult.uniqueCardsCount} itens):</span>
                    <span>Qtd / Seção</span>
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1 pr-1 custom-scrollbar text-xs font-mono">
                    {validationResult.parsedCards.map((c, i) => (
                      <div 
                        key={i} 
                        className="bg-slate-950/70 p-2 rounded-xl border border-white/5 flex items-center justify-between"
                      >
                        <div className="truncate pr-2">
                          <span className="text-white font-bold">{c.name}</span>
                          {c.code && (
                            <span className="text-slate-500 text-[10px] ml-1.5">[{c.code}]</span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 shrink-0">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                            {c.section}
                          </span>
                          <span className="text-purple-400 font-bold">
                            x{c.quantity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resultado da Busca: 100% Sucesso */}
                {importResult && importResult.success && importResult.notFoundItems.length === 0 && (
                  <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 space-y-2 animate-fadeIn">
                    <div className="flex items-center space-x-2.5">
                      <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                        <i className="fas fa-check-double text-sm"></i>
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-emerald-300 uppercase tracking-wider">
                          Importação Concluída com Sucesso!
                        </h4>
                        <p className="text-[11px] text-slate-300">
                          Todas as <strong>{importResult.totalCards} cartas</strong> foram localizadas e adicionadas ao baralho.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Resultado Parcial: Algumas cartas não encontradas */}
                {importResult && importResult.success && importResult.notFoundItems.length > 0 && (
                  <div className="p-4 rounded-2xl bg-amber-500/10 border border-amber-500/30 space-y-3 animate-fadeIn">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start space-x-2.5">
                        <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0 mt-0.5">
                          <i className="fas fa-exclamation-triangle text-sm"></i>
                        </div>
                        <div className="space-y-0.5">
                          <h4 className="text-xs font-black text-amber-300 uppercase tracking-wider">
                            Importação Parcial ({importResult.totalCards} cartas adicionadas)
                          </h4>
                          <p className="text-[11px] text-slate-300">
                            <strong>{importResult.notFoundItems.length} {importResult.notFoundItems.length === 1 ? 'carta não foi localizada' : 'cartas não foram localizadas'}</strong> no catálogo e precisam de revisão.
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleCopyMissingCards}
                        className="px-2.5 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1.5 transition-all shrink-0 active:scale-95"
                      >
                        <i className={hasCopiedMissing ? "fas fa-check text-emerald-400" : "fas fa-copy"}></i>
                        <span>{hasCopiedMissing ? 'Copiado!' : 'Copiar Faltantes'}</span>
                      </button>
                    </div>

                    <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-amber-400/80 block">
                        Cartas não localizadas:
                      </span>
                      <div className="max-h-28 overflow-y-auto space-y-1 pr-1 custom-scrollbar text-xs font-mono">
                        {importResult.notFoundItems.map((nc, idx) => (
                          <div key={idx} className="bg-slate-950/70 p-2 rounded-xl border border-amber-500/20 flex items-center justify-between">
                            <span className="text-slate-300 truncate font-semibold">
                              {nc.name} {nc.code ? `[${nc.code}]` : ''}
                            </span>
                            <div className="flex items-center space-x-2 shrink-0">
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400">
                                {nc.section}
                              </span>
                              <span className="text-amber-400 font-bold">
                                x{nc.quantity}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Resultado Totalmente Infrutífero: 0 Cartas Encontradas */}
                {importResult && !importResult.success && (
                  <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/30 space-y-3 animate-fadeIn">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start space-x-2.5">
                        <div className="w-8 h-8 rounded-xl bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 shrink-0 mt-0.5">
                          <i className="fas fa-times-circle text-sm"></i>
                        </div>
                        <div className="space-y-0.5">
                          <h4 className="text-xs font-black text-red-300 uppercase tracking-wider">
                            Falha na Busca das Cartas
                          </h4>
                          <p className="text-[11px] text-slate-300">
                            Nenhuma das {validationResult.parsedCards.length} cartas foi encontrada na base de dados do jogo.
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleCopyMissingCards}
                        className="px-2.5 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1.5 transition-all shrink-0 active:scale-95"
                      >
                        <i className={hasCopiedMissing ? "fas fa-check text-emerald-400" : "fas fa-copy"}></i>
                        <span>{hasCopiedMissing ? 'Copiado!' : 'Copiar Lista'}</span>
                      </button>
                    </div>

                    <div className="p-2.5 rounded-xl bg-slate-950/60 border border-white/5 space-y-1 text-[11px] text-slate-400">
                      <p className="font-bold text-slate-300 flex items-center space-x-1.5">
                        <i className="fas fa-lightbulb text-amber-400 text-xs"></i>
                        <span>Possíveis causas e soluções:</span>
                      </p>
                      <ul className="list-disc list-inside space-y-0.5 pl-1 text-[10px] text-slate-400">
                        <li>Certifique-se de que os códigos seguem o padrão oficial (ex: <code>OP01-001</code>, <code>ST14-006</code>).</li>
                        <li>Verifique se o jogo selecionado no topo ({gameName}) corresponde ao baralho colado.</li>
                        <li>Você também pode tentar o formato <strong>Lista Textual</strong> com os nomes das cartas.</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error Banner Geral (caso ocorra erro antes ou durante a etapa) */}
            {errorBanner && (!importResult || importResult.success) && (
              <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center space-x-2.5 text-red-400 text-xs font-bold animate-fadeIn">
                <i className="fas fa-exclamation-circle text-sm shrink-0"></i>
                <span>{errorBanner}</span>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            {currentStep === 1 ? (
              <button
                type="button"
                onClick={onClose}
                disabled={isFetchingCards}
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-black uppercase tracking-wider rounded-xl transition-all disabled:opacity-50"
              >
                Cancelar
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setCurrentStep(1);
                  setErrorBanner(null);
                  setImportResult(null);
                }}
                disabled={isFetchingCards}
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-black uppercase tracking-wider rounded-xl transition-all disabled:opacity-50 flex items-center space-x-1.5"
              >
                <i className="fas fa-arrow-left text-[10px]"></i>
                <span>Voltar / Editar Lista</span>
              </button>
            )}

            <div className="flex items-center space-x-3">
              {currentStep === 1 ? (
                <button
                  id="import-deck-validate-button"
                  type="button"
                  onClick={handleValidateStep}
                  disabled={!isFormatAvailable || !rawText.trim()}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/30 transition-all flex items-center space-x-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                >
                  <i className="fas fa-check-circle"></i>
                  <span>1. Validar Formato</span>
                </button>
              ) : (
                <>
                  {importResult && importResult.success && importResult.notFoundItems.length > 0 ? (
                    <button
                      id="import-deck-finish-button"
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-purple-600/30 transition-all flex items-center space-x-2 active:scale-95"
                    >
                      <i className="fas fa-check"></i>
                      <span>Concluir e Ver Baralho</span>
                    </button>
                  ) : (
                    <button
                      id="import-deck-fetch-button"
                      type="button"
                      onClick={handleFetchCardsStep}
                      disabled={isFetchingCards || !validationResult?.isValid}
                      className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-lg shadow-emerald-600/30 transition-all flex items-center space-x-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95"
                    >
                      {isFetchingCards ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          <span>Buscando Cartas...</span>
                        </>
                      ) : importResult && !importResult.success ? (
                        <>
                          <i className="fas fa-redo"></i>
                          <span>Tentar Buscar Novamente</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-search-plus"></i>
                          <span>2. Buscar Cartas e Importar</span>
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
